/*  Public Certificate Verify Page — iter100.113s.
    Reachable at /verify/:certNumber. NO AUTH REQUIRED. Anyone who has a
    doctor's certificate number can land here (e.g. a LinkedIn viewer
    scanning the QR on the PDF) and see:
        • valid?
        • course + doctor name
        • issued / expires dates
        • CE credits + authority (if configured)
    Renders a friendly Invisique-branded landing for growth loop —
    prospects clicking through should feel this is the real Invisique.  */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
    CheckCircle, ShieldCheck, Warning, CalendarBlank, Trophy,
    GraduationCap, Sparkle, ArrowRight,
} from "@phosphor-icons/react";


const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");


export default function CertificateVerifyPage() {
    const { certNumber } = useParams();
    const [state, setState] = useState({ status: "loading", data: null, error: null });

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/api/public/certificates/verify/${encodeURIComponent(certNumber)}`);
                setState({ status: "ok", data, error: null });
            } catch (e) {
                setState({ status: "error", data: null, error: e?.response?.data?.detail || "Network error" });
            }
        };
        if (certNumber) load();
    }, [certNumber]);

    useEffect(() => {
        const prev = document.title;
        document.title = `Verify · ${certNumber} · Invisique™`;
        return () => { document.title = prev; };
    }, [certNumber]);

    const isValid = state.data?.valid === true;
    const isInvalid = state.status === "ok" && state.data?.valid === false;

    return (
        <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-white" data-testid="cert-verify-page">
            <TopNav />
            <main className="max-w-4xl mx-auto px-6 py-12 md:py-20">
                {state.status === "loading" && <VerifyLoading certNumber={certNumber} />}
                {state.status === "error" && <VerifyError error={state.error} />}
                {isInvalid && <VerifyNotFound cert={state.data} certNumber={certNumber} />}
                {isValid && <VerifyValid cert={state.data} />}
                <GrowthCTA />
            </main>
            <PageFooter />
        </div>
    );
}


function TopNav() {
    return (
        <header className="border-b border-border/60 bg-white/70 backdrop-blur-sm">
            <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
                <Link to="/" className="inline-flex items-center gap-2 no-underline group" data-testid="cert-verify-brand-link">
                    <div className="w-8 h-8 rounded-md bg-gradient-to-br from-primary to-primary/80 grid place-items-center text-primary-foreground shadow-sm">
                        <GraduationCap size={16} weight="fill" />
                    </div>
                    <span className="font-display text-lg font-bold text-foreground tracking-tight group-hover:text-primary transition-colors">
                        Invisique&trade;
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground pl-2 border-l border-border/60 ml-1">
                        Certificate Verify
                    </span>
                </Link>
                <Link
                    to="/"
                    className="text-xs font-semibold text-primary hover:underline no-underline"
                    data-testid="cert-verify-visit-invisique-link"
                >
                    Visit Invisique <ArrowRight size={11} weight="bold" className="inline -mt-0.5 ml-0.5" />
                </Link>
            </div>
        </header>
    );
}


function VerifyLoading({ certNumber }) {
    return (
        <div className="bg-white rounded-2xl bento-shadow p-8 text-center" data-testid="cert-verify-loading">
            <div className="w-14 h-14 rounded-2xl bg-secondary border border-border mx-auto mb-3 grid place-items-center">
                <Sparkle size={28} weight="duotone" className="text-muted-foreground animate-pulse" />
            </div>
            <h1 className="font-display text-xl font-bold">Verifying certificate</h1>
            <p className="text-sm text-muted-foreground mt-1 font-mono">{certNumber}</p>
        </div>
    );
}


function VerifyError({ error }) {
    return (
        <div className="bg-white rounded-2xl bento-shadow p-8 text-center" data-testid="cert-verify-error">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 border border-slate-200 mx-auto mb-3 grid place-items-center">
                <Warning size={28} weight="duotone" className="text-slate-500" />
            </div>
            <h1 className="font-display text-xl font-bold">Couldn&apos;t verify right now</h1>
            <p className="text-sm text-muted-foreground mt-1">{error}. Try again in a moment.</p>
        </div>
    );
}


function VerifyNotFound({ cert, certNumber }) {
    return (
        <div className="bg-white rounded-2xl bento-shadow p-10 text-center max-w-2xl mx-auto" data-testid="cert-verify-not-found">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 border border-slate-200 mx-auto mb-4 grid place-items-center">
                <Warning size={32} weight="duotone" className="text-slate-500" />
            </div>
            <h1 className="font-display text-2xl font-bold text-foreground">Certificate not verified</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
                {cert?.reason || "This certificate number couldn't be found in our records. It may have expired, been revoked, or the number is mistyped."}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono text-muted-foreground bg-secondary/40 border border-border px-3 py-1.5 rounded-md">
                <span className="text-[10px] uppercase tracking-wider">Cert No.</span>
                {certNumber}
            </div>
        </div>
    );
}


function VerifyValid({ cert }) {
    const statusMeta = STATUS_META[cert.status] || STATUS_META.active;
    const StatusIcon = statusMeta.icon;
    const issuedDate = useMemo(() => formatDate(cert.issued_at), [cert.issued_at]);
    const expiresDate = useMemo(() => formatDate(cert.expires_at), [cert.expires_at]);

    return (
        <div className="bg-white rounded-2xl bento-shadow overflow-hidden" data-testid="cert-verify-valid">
            {/* Top status band */}
            <div className={`px-6 py-4 flex items-center gap-3 border-b ${statusMeta.bandCls}`}>
                <div className={`w-10 h-10 rounded-xl ${statusMeta.iconTileCls} grid place-items-center shrink-0 shadow-sm`}>
                    <StatusIcon size={22} weight="fill" />
                </div>
                <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Verification result</div>
                    <div className={`font-display text-lg font-bold ${statusMeta.textCls}`} data-testid="cert-verify-status-label">
                        {statusMeta.label}
                    </div>
                </div>
                <div className="hidden sm:block text-right">
                    <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">Cert No.</div>
                    <div className="font-mono text-sm font-semibold" data-testid="cert-verify-cert-number">{cert.cert_number}</div>
                </div>
            </div>

            {/* Body */}
            <div className="p-6 md:p-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-rose-600">Awarded to</div>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mt-1 tracking-tight" data-testid="cert-verify-doctor-name">
                    {cert.doctor_name}
                </h1>
                <div className="mt-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Invisique&trade; Certified Provider
                </div>

                <div className="mt-6 p-5 rounded-xl bg-rose-50/70 border border-rose-100">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-rose-700">Course completed</div>
                    <div className="font-display text-xl font-bold text-foreground mt-1" data-testid="cert-verify-course-title">
                        {cert.course_title}
                    </div>
                    {cert.ce_credits && (
                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-rose-900 bg-white border border-rose-200 px-2.5 py-1 rounded-full">
                            <Trophy size={11} weight="fill" className="text-rose-500" />
                            <span>{cert.ce_credits} CE credit{Number(cert.ce_credits) === 1 ? "" : "s"}{cert.ce_authority ? ` · ${cert.ce_authority}` : ""}</span>
                        </div>
                    )}
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <MetaTile Icon={CalendarBlank} label="Issued on" value={issuedDate} testid="cert-verify-issued" />
                    <MetaTile Icon={CalendarBlank} label="Valid until" value={expiresDate} testid="cert-verify-expires" />
                </div>

                <div className="mt-6 p-3 rounded-lg border border-dashed border-border bg-secondary/30 flex items-start gap-2 text-[11px] text-muted-foreground">
                    <ShieldCheck size={13} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                    <span>
                        This certificate was issued directly by the Invisique&trade; Learning Academy and cross-checked
                        against our authoritative records. If you have questions or believe a certificate has been
                        misused, contact <a className="underline text-primary" href="mailto:academy@invisique.com">academy@invisique.com</a>.
                    </span>
                </div>
            </div>
        </div>
    );
}


function MetaTile({ Icon, label, value, testid }) {
    return (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid={testid}>
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-primary/10 border border-primary/15 grid place-items-center shrink-0">
                    <Icon size={13} weight="duotone" className="text-primary" />
                </div>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
                    <div className="text-sm font-semibold text-foreground">{value}</div>
                </div>
            </div>
        </div>
    );
}


function GrowthCTA() {
    return (
        <div className="mt-8 max-w-2xl mx-auto bg-primary/5 border border-primary/15 rounded-2xl p-5 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground grid place-items-center shrink-0 shadow-sm">
                <GraduationCap size={18} weight="fill" />
            </div>
            <div className="flex-1">
                <div className="font-display font-bold text-sm text-foreground">Ready to earn your own Invisique&trade; certification?</div>
                <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Join the Invisique Learning Academy — clinical, business, and clear-aligner mastery programs designed
                    for orthodontists, GPs, and dental clinics.
                </div>
                <div className="mt-3">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 h-8 px-4 rounded-md bg-primary text-primary-foreground text-xs font-bold no-underline hover:bg-primary/90 transition-colors"
                        data-testid="cert-verify-explore-cta"
                    >
                        Explore the Academy <ArrowRight size={11} weight="bold" />
                    </Link>
                </div>
            </div>
        </div>
    );
}


function PageFooter() {
    return (
        <footer className="border-t border-border/60 bg-white/50 mt-8">
            <div className="max-w-6xl mx-auto px-6 h-12 flex items-center justify-between text-[10px] text-muted-foreground">
                <span>© Invisique&trade; · Verified via the public Certificate Registry</span>
                <span className="font-mono">v1 · public verify</span>
            </div>
        </footer>
    );
}


const STATUS_META = {
    active: {
        label: "Valid & Active",
        icon: CheckCircle,
        bandCls: "bg-emerald-50 border-emerald-100 text-emerald-900",
        iconTileCls: "bg-emerald-500 text-white",
        textCls: "text-emerald-900",
    },
    expiring: {
        label: "Valid · Expires Soon",
        icon: ShieldCheck,
        bandCls: "bg-amber-50 border-amber-100 text-amber-900",
        iconTileCls: "bg-amber-500 text-white",
        textCls: "text-amber-900",
    },
    expired: {
        label: "Expired",
        icon: Warning,
        bandCls: "bg-slate-100 border-slate-200 text-slate-800",
        iconTileCls: "bg-slate-500 text-white",
        textCls: "text-slate-800",
    },
};


function formatDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
    catch { return "—"; }
}
